export type CourseActivityStatus = 'upcoming' | 'active' | 'completed' | 'inactive'

export interface CourseProgressSummaryJSON {
  percentComplete?: number | string | null
  modulesTotal?: number | null
  modulesCompleted?: number | null
  courseProgressRow?: CourseProgressRowJSON | null
}

export interface CourseProgressRowJSON {
  id: string
  learner_id: string
  course_id: string
  percent_complete?: number | string | null
  modules_total?: number | null
  modules_completed?: number | null
  last_updated?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface ModuleProgressJSON {
  id: string
  learner_id: string
  course_id: string
  module_id: string
  status?: string | null
  time_spent_seconds?: number | null
  started_at?: string | null
  completed_at?: string | null
  meta?: Record<string, unknown> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface ResourceProgressJSON {
  id: string
  learner_id: string
  resource_id: string
  course_id: string
  played_seconds?: number | null
  completed?: boolean | null
  meta?: Record<string, unknown> | null
  last_played_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export class CourseProgressRowModel {
  readonly id: string
  readonly learnerId: string
  readonly courseId: string
  readonly percentComplete?: number
  readonly modulesTotal?: number
  readonly modulesCompleted?: number
  readonly lastUpdated?: string
  readonly createdAt?: string
  readonly updatedAt?: string

  constructor(options: {
    id: string
    learnerId: string
    courseId: string
    percentComplete?: number
    modulesTotal?: number
    modulesCompleted?: number
    lastUpdated?: string
    createdAt?: string
    updatedAt?: string
  }) {
    this.id = options.id
    this.learnerId = options.learnerId
    this.courseId = options.courseId
    this.percentComplete = options.percentComplete
    this.modulesTotal = options.modulesTotal
    this.modulesCompleted = options.modulesCompleted
    this.lastUpdated = options.lastUpdated
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
  }

  static fromJSON(json: CourseProgressRowJSON): CourseProgressRowModel {
    return new CourseProgressRowModel({
      id: json.id,
      learnerId: json.learner_id,
      courseId: json.course_id,
      percentComplete: parsePercentage(json.percent_complete),
      modulesTotal: json.modules_total ?? undefined,
      modulesCompleted: json.modules_completed ?? undefined,
      lastUpdated: json.last_updated ?? undefined,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
    })
  }
}

export class CourseProgressSummaryModel {
  readonly percentComplete: number
  readonly modulesTotal?: number
  readonly modulesCompleted?: number
  readonly courseProgressRow?: CourseProgressRowModel

  constructor(options: {
    percentComplete?: number
    modulesTotal?: number
    modulesCompleted?: number
    courseProgressRow?: CourseProgressRowModel
  }) {
    this.percentComplete = clampPercentage(options.percentComplete)
    this.modulesTotal = options.modulesTotal
    this.modulesCompleted = options.modulesCompleted
    this.courseProgressRow = options.courseProgressRow
  }

  static fromJSON(json?: CourseProgressSummaryJSON | null): CourseProgressSummaryModel | undefined {
    if (!json) return undefined
    return new CourseProgressSummaryModel({
      percentComplete: parsePercentage(json.percentComplete),
      modulesTotal: json.modulesTotal ?? undefined,
      modulesCompleted: json.modulesCompleted ?? undefined,
      courseProgressRow: json.courseProgressRow ? CourseProgressRowModel.fromJSON(json.courseProgressRow) : undefined,
    })
  }
}

export class ModuleProgressModel {
  readonly id: string
  readonly learnerId: string
  readonly courseId: string
  readonly moduleId: string
  readonly status?: string
  readonly timeSpentSeconds?: number
  readonly startedAt?: string
  readonly completedAt?: string
  readonly meta?: Record<string, unknown>
  readonly createdAt?: string
  readonly updatedAt?: string

  constructor(options: {
    id: string
    learnerId: string
    courseId: string
    moduleId: string
    status?: string
    timeSpentSeconds?: number
    startedAt?: string
    completedAt?: string
    meta?: Record<string, unknown>
    createdAt?: string
    updatedAt?: string
  }) {
    this.id = options.id
    this.learnerId = options.learnerId
    this.courseId = options.courseId
    this.moduleId = options.moduleId
    this.status = options.status
    this.timeSpentSeconds = options.timeSpentSeconds
    this.startedAt = options.startedAt
    this.completedAt = options.completedAt
    this.meta = options.meta
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
  }

  static fromJSON(json: ModuleProgressJSON): ModuleProgressModel {
    return new ModuleProgressModel({
      id: json.id,
      learnerId: json.learner_id,
      courseId: json.course_id,
      moduleId: json.module_id,
      status: json.status ?? undefined,
      timeSpentSeconds: json.time_spent_seconds ?? undefined,
      startedAt: json.started_at ?? undefined,
      completedAt: json.completed_at ?? undefined,
      meta: json.meta ?? undefined,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
    })
  }

  get isCompleted(): boolean {
    return (this.status ?? '').toLowerCase() === 'completed' || Boolean(this.completedAt)
  }
}

export class ResourceProgressModel {
  readonly id: string
  readonly learnerId: string
  readonly resourceId: string
  readonly courseId: string
  readonly playedSeconds: number
  readonly completed: boolean
  readonly meta?: Record<string, unknown>
  readonly lastPlayedAt?: string
  readonly createdAt?: string
  readonly updatedAt?: string

  constructor(options: {
    id: string
    learnerId: string
    resourceId: string
    courseId: string
    playedSeconds?: number
    completed?: boolean
    meta?: Record<string, unknown>
    lastPlayedAt?: string
    createdAt?: string
    updatedAt?: string
  }) {
    this.id = options.id
    this.learnerId = options.learnerId
    this.resourceId = options.resourceId
    this.courseId = options.courseId
    this.playedSeconds = Math.max(0, options.playedSeconds ?? 0)
    this.completed = options.completed ?? false
    this.meta = options.meta
    this.lastPlayedAt = options.lastPlayedAt
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
  }

  static fromJSON(json: ResourceProgressJSON): ResourceProgressModel {
    return new ResourceProgressModel({
      id: json.id,
      learnerId: json.learner_id,
      resourceId: json.resource_id,
      courseId: json.course_id,
      playedSeconds: json.played_seconds ?? undefined,
      completed: json.completed ?? undefined,
      meta: json.meta ?? undefined,
      lastPlayedAt: json.last_played_at ?? undefined,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
    })
  }
}

function parsePercentage(input?: number | string | null): number | undefined {
  if (input === null || input === undefined) return undefined
  const value = typeof input === 'string' ? Number.parseFloat(input) : input
  if (!Number.isFinite(value)) return undefined
  return value
}

function clampPercentage(value?: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0
  return Math.min(100, Math.max(0, Math.round(value)))
}

export interface CourseModuleJSON {
  id: string
  course_id: string
  title: string
  description?: string | null
  order?: number | null
  is_active?: boolean | null
  created_at?: string | null
  updated_at?: string | null
  resources?: CourseResourceJSON[] | null
  progress?: ModuleProgressJSON | null
}

export interface CourseResourceJSON {
  id: string
  module_id?: string | null
  course_id: string
  title: string
  description?: string | null
  type?: string | null
  storage_key?: string | null
  url?: string | null
  file_size?: number | null
  mime_type?: string | null
  order?: number | null
  processing_status?: string | null
  is_active?: boolean | null
  created_at?: string | null
  updated_at?: string | null
  progress?: ResourceProgressJSON | null
}

export interface CourseJSON {
  id: string
  programme_id?: string | null
  department_id?: string | null
  faculty_id?: string | null
  faculty_name?: string | null
  programme_title?: string | null
  department_name?: string | null
  title: string
  description?: string | null
  code?: string | null
  credits?: number | null
  instructor_id?: string | null
  level?: string | null
  semester?: string | null
  stream_library_id?: string | null
  is_active?: boolean | null
  start_date?: string | null
  end_date?: string | null
  created_at?: string | null
  updated_at?: string | null
  modules?: CourseModuleJSON[] | null
  resources?: CourseResourceJSON[] | null
}

function normalizeDateInput(value?: string | null): string | undefined {
  if (!value) return undefined
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value ?? undefined
  }
  return parsed.toISOString().slice(0, 10)
}

export class CourseResourceModel {
  readonly id: string
  readonly courseId: string
  readonly moduleId?: string
  readonly title: string
  readonly description?: string
  readonly type?: string
  readonly storageKey?: string
  readonly url?: string
  readonly fileSize?: number
  readonly mimeType?: string
  readonly order?: number
  readonly processingStatus?: string
  readonly isActive: boolean
  readonly createdAt?: string
  readonly updatedAt?: string
  readonly progress?: ResourceProgressModel

  constructor(options: {
    id: string
    courseId: string
    moduleId?: string
    title: string
    description?: string
    type?: string
    storageKey?: string
    url?: string
    fileSize?: number
    mimeType?: string
    order?: number
    processingStatus?: string
    isActive?: boolean
    createdAt?: string
    updatedAt?: string
    progress?: ResourceProgressModel
  }) {
    this.id = options.id
    this.courseId = options.courseId
    this.moduleId = options.moduleId
    this.title = options.title
    this.description = options.description
    this.type = options.type
    this.storageKey = options.storageKey
    this.url = options.url
    this.fileSize = options.fileSize
    this.mimeType = options.mimeType
    this.order = options.order
    this.processingStatus = options.processingStatus
    this.isActive = options.isActive ?? true
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
    this.progress = options.progress
  }

  static fromJSON(json: CourseResourceJSON): CourseResourceModel {
    return new CourseResourceModel({
      id: json.id,
      courseId: json.course_id,
      moduleId: json.module_id ?? undefined,
      title: json.title,
      description: json.description ?? undefined,
      type: json.type ?? undefined,
      storageKey: json.storage_key ?? undefined,
      url: json.url ?? undefined,
      fileSize: json.file_size ?? undefined,
      mimeType: json.mime_type ?? undefined,
      order: json.order ?? undefined,
      processingStatus: json.processing_status ?? undefined,
      isActive: json.is_active ?? true,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
      progress: json.progress ? ResourceProgressModel.fromJSON(json.progress) : undefined,
    })
  }
}

export class CourseModuleModel {
  readonly id: string
  readonly courseId: string
  readonly title: string
  readonly description?: string
  readonly order?: number
  readonly isActive: boolean
  readonly createdAt?: string
  readonly updatedAt?: string
  readonly resources: CourseResourceModel[]
  readonly progress?: ModuleProgressModel

  constructor(options: {
    id: string
    courseId: string
    title: string
    description?: string
    order?: number
    isActive?: boolean
    createdAt?: string
    updatedAt?: string
    resources?: CourseResourceModel[]
    progress?: ModuleProgressModel
  }) {
    this.id = options.id
    this.courseId = options.courseId
    this.title = options.title
    this.description = options.description
    this.order = options.order
    this.isActive = options.isActive ?? true
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
    this.resources = options.resources ?? []
    this.progress = options.progress
  }

  static fromJSON(json: CourseModuleJSON, resources: CourseResourceJSON[] = []): CourseModuleModel {
    const moduleResourcesSource = Array.isArray(json.resources)
      ? json.resources.filter((resource): resource is CourseResourceJSON => Boolean(resource))
      : resources.filter((resource) => resource?.module_id === json.id)

    const moduleResources = moduleResourcesSource.map((resource) => CourseResourceModel.fromJSON(resource))

    return new CourseModuleModel({
      id: json.id,
      courseId: json.course_id,
      title: json.title,
      description: json.description ?? undefined,
      order: json.order ?? undefined,
      isActive: json.is_active ?? true,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
      resources: moduleResources,
      progress: json.progress ? ModuleProgressModel.fromJSON(json.progress) : undefined,
    })
  }
}

export class CourseModel {
  readonly id: string
  readonly programmeId?: string
  readonly departmentId?: string
  readonly facultyId?: string
  readonly title: string
  readonly description?: string
  readonly code?: string
  readonly credits?: number
  readonly instructorId?: string
  readonly level?: string
  readonly semester?: string
  readonly streamLibraryId?: string
  readonly isActive: boolean
  readonly startDate?: string
  readonly endDate?: string
  readonly createdAt?: string
  readonly updatedAt?: string
  readonly modules: CourseModuleModel[]
  readonly resources: CourseResourceModel[]
  readonly programmeTitle?: string
  readonly departmentName?: string
  readonly facultyName?: string

  constructor(options: {
    id: string
    programmeId?: string
    departmentId?: string
    facultyId?: string
    title: string
    description?: string
    code?: string
    credits?: number
    instructorId?: string
    level?: string
    semester?: string
    streamLibraryId?: string
    isActive?: boolean
    startDate?: string
    endDate?: string
    createdAt?: string
    updatedAt?: string
    modules?: CourseModuleModel[]
    resources?: CourseResourceModel[]
    programmeTitle?: string
    departmentName?: string
    facultyName?: string
  }) {
    this.id = options.id
    this.programmeId = options.programmeId
    this.departmentId = options.departmentId
    this.facultyId = options.facultyId
    this.title = options.title
    this.description = options.description
    this.code = options.code
    this.credits = options.credits
    this.instructorId = options.instructorId
    this.level = options.level
    this.semester = options.semester
    this.streamLibraryId = options.streamLibraryId
    this.isActive = options.isActive ?? true
    this.startDate = options.startDate
    this.endDate = options.endDate
    this.createdAt = options.createdAt
    this.updatedAt = options.updatedAt
    this.modules = options.modules ?? []
    this.resources = options.resources ?? []
    this.programmeTitle = options.programmeTitle
    this.departmentName = options.departmentName
    this.facultyName = options.facultyName
  }

  static fromJSON(json: CourseJSON): CourseModel {
    const resources = Array.isArray(json.resources)
      ? json.resources
          .filter((resource): resource is CourseResourceJSON => Boolean(resource))
          .map((resource) => CourseResourceModel.fromJSON(resource))
      : []

    const modules = Array.isArray(json.modules)
      ? json.modules
          .filter((module): module is CourseModuleJSON => Boolean(module))
          .map((module) => CourseModuleModel.fromJSON(module, json.resources ?? []))
      : []

    return new CourseModel({
      id: json.id,
      programmeId: json.programme_id ?? undefined,
      departmentId: json.department_id ?? undefined,
      facultyId: json.faculty_id ?? undefined,
      title: json.title,
      description: json.description ?? undefined,
      code: json.code ?? undefined,
      credits: json.credits ?? undefined,
      instructorId: json.instructor_id ?? undefined,
      level: json.level ?? undefined,
      semester: json.semester ?? undefined,
      streamLibraryId: json.stream_library_id ?? undefined,
      isActive: json.is_active ?? true,
      startDate: normalizeDateInput(json.start_date),
      endDate: normalizeDateInput(json.end_date),
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
      modules,
      resources,
      programmeTitle: json.programme_title ?? undefined,
      departmentName: json.department_name ?? undefined,
      facultyName: json.faculty_name ?? undefined,
    })
  }

  /**
   * Returns a human friendly label for course duration if start and end dates exist.
   */
  get durationLabel(): string | undefined {
    if (!this.startDate || !this.endDate) return undefined
    const start = new Date(this.startDate)
    const end = new Date(this.endDate)
    if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf())) return undefined
    const diffMs = end.getTime() - start.getTime()
    if (diffMs <= 0) return undefined
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'}`
    const diffWeeks = Math.round(diffDays / 7)
    if (Number.isInteger(diffWeeks)) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'}`
    return `${diffWeeks.toFixed(1)} weeks`
  }

  /**
   * Derives a high-level course status using activation flag and date range.
   */
  get activityStatus(): CourseActivityStatus {
    if (!this.isActive) return 'inactive'
    const now = Date.now()
    const start = this.startDate ? new Date(this.startDate).getTime() : undefined
    const end = this.endDate ? new Date(this.endDate).getTime() : undefined

    if (start && start > now) {
      return 'upcoming'
    }

    if (end && end < now) {
      return 'completed'
    }

    return 'active'
  }

  /**
   * Filters course-level resources (not attached to a module).
   */
  get courseLevelResources(): CourseResourceModel[] {
    return this.resources.filter((resource) => !resource.moduleId)
  }

  /**
   * Returns resources for a specific module id.
   */
  getResourcesForModule(moduleId: string): CourseResourceModel[] {
    return this.resources.filter((resource) => resource.moduleId === moduleId)
  }
}
